import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';

import { logError } from '@savgroup-front-common/configuration/src/appInsights/AppInsights';
import {
  fetchDynamicIntl,
  fetchIntl,
} from '@savgroup-front-common/core/src/components/IntlManager/IntlManager.helpers';
import { selectLocale } from '@savgroup-front-common/core/src/domains/i18n/selectors';
import useCommonTypedSelector from '@savgroup-front-common/core/src/domains/useCommonTypedSelector';
import {
  defaultLanguage,
  getConfigLanguage,
  IntlGlobalProvider,
} from '@savgroup-front-common/core/src/helpers/intl';
import { BaseLoader } from '@savgroup-front-common/core/src/molecules/BaseLoader';

const INTL_FETCH_TIMEOUT_MS = 4_000;

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | undefined> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<undefined>((resolve) => {
    timeoutId = setTimeout(() => resolve(undefined), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

/**
 * Variante POC : ne bloque pas l’UI si le CDN traductions ne répond pas (fetch pendant).
 */
const PocIntlManager: FC<PropsWithChildren> = ({ children }) => {
  const locale = useCommonTypedSelector(selectLocale) || defaultLanguage;
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessages] = useState<Record<string, string>>({});
  const [dynamicMessages, setDynamicMessages] = useState<Record<string, string>>({});

  const config = useMemo(() => getConfigLanguage(locale), [locale]);

  useEffect(() => {
    let cancelled = false;

    const loadTranslations = async () => {
      setIsLoading(true);

      const [staticResult, dynamicResult] = await Promise.all([
        withTimeout(fetchIntl(locale), INTL_FETCH_TIMEOUT_MS),
        withTimeout(fetchDynamicIntl(locale), INTL_FETCH_TIMEOUT_MS),
      ]);

      if (cancelled) {
        return;
      }

      if (staticResult && !staticResult.failure) {
        setMessages(staticResult.value);
      } else if (staticResult?.failure) {
        logError(new Error('fetchIntl failed'), { operation: 'Fetch intl (POC)' });
        setMessages({});
      }

      if (dynamicResult && !dynamicResult.failure) {
        setDynamicMessages(dynamicResult.value);
      } else if (dynamicResult?.failure) {
        setDynamicMessages({});
      }

      setIsLoading(false);
    };

    void loadTranslations();

    return () => {
      cancelled = true;
    };
  }, [locale]);

  return (
    <IntlProvider
      locale={locale}
      messages={{ ...messages, ...dynamicMessages }}
      formats={config.formats}
      defaultLocale={defaultLanguage}
    >
      <IntlGlobalProvider isSuperAdmin={false}>
        {isLoading ? <BaseLoader /> : children}
      </IntlGlobalProvider>
    </IntlProvider>
  );
};

export default PocIntlManager;
